import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Mail, Loader2, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

import API_URL from '../config/api';

export default function VerifyEmail() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState(token ? 'verifying' : 'waiting'); // verifying | success | error | waiting
  const [resending, setResending] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) return;
    const verify = async () => {
      try {
        await axios.get(`${API_URL}/api/auth/verify-email?token=${token}`);
        setStatus('success');
        await refreshUser();
        toast.success('Email verified!');
        setTimeout(() => navigate('/onboarding'), 2000);
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Verification failed');
      }
    };
    verify();
  }, [token, refreshUser, navigate]);

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, { withCredentials: true });
      toast.success('Verification email sent! Check your inbox.');
    } catch (err) {
      toast.error('Failed to resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  if (user?.is_email_verified && !token) {
    navigate(user?.onboarding_complete ? '/discover' : '/onboarding');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Heart className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
          <span className="text-2xl font-serif font-medium text-[#2D2D2A]">LusoLove</span>
        </div>

        {status === 'verifying' && (
          <div className="py-12">
            <Loader2 className="w-12 h-12 text-[#6E1A24] animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-serif text-[#2D2D2A] mb-2">Verifying your email...</h1>
            <p className="text-sm text-[#6C6C67]">This will only take a moment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-12">
            <div className="w-16 h-16 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#2D6A4F]" />
            </div>
            <h1 className="text-xl font-serif text-[#2D2D2A] mb-2">Email Verified!</h1>
            <p className="text-sm text-[#6C6C67] mb-6">Your email has been verified. Redirecting you now...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="py-12">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-serif text-[#2D2D2A] mb-2">Verification Failed</h1>
            <p className="text-sm text-red-600 mb-6">{errorMsg}</p>
            <Button onClick={handleResend} disabled={resending} className="bg-[#6E1A24] text-white rounded-full px-6 py-3" data-testid="resend-verification">
              {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Send New Verification Link
            </Button>
          </div>
        )}

        {status === 'waiting' && (
          <div className="py-8">
            <div className="w-20 h-20 rounded-full bg-[#6E1A24]/10 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-[#6E1A24]" />
            </div>
            <h1 className="text-2xl font-serif text-[#2D2D2A] mb-3">Check Your Email</h1>
            <p className="text-sm text-[#6C6C67] mb-2">
              We sent a verification link to
            </p>
            <p className="text-sm font-semibold text-[#2D2D2A] mb-6">{user?.email}</p>
            <p className="text-sm text-[#6C6C67] mb-8">
              Click the link in the email to verify your account and unlock all features.
            </p>

            <div className="bg-[#F5F2EB] rounded-2xl p-5 mb-6 text-left">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-2">While you wait, you can:</p>
              <ul className="text-sm text-[#6C6C67] space-y-1.5">
                <li>Browse profiles and explore</li>
                <li>Complete your profile</li>
                <li>Swiping and messaging require verification</li>
              </ul>
            </div>

            <button
              onClick={handleResend}
              disabled={resending}
              className="text-sm text-[#6C6C67] hover:text-[#6E1A24] flex items-center justify-center gap-2 mx-auto mb-4"
              data-testid="resend-verification-link"
            >
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Resend verification email
            </button>

            <div className="flex gap-3 mt-6">
              <Button onClick={() => navigate('/onboarding')} className="flex-1 bg-[#6E1A24] text-white rounded-full py-3" data-testid="continue-to-onboarding">
                Continue to Profile
              </Button>
            </div>

            <div className="mt-6 pt-6 border-t border-[#E5E3DB]">
              <button onClick={logout} className="text-xs text-[#9C9C97] hover:text-[#6E1A24]" data-testid="verify-logout">
                Use a different account
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
